import { Link, createRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { useScheduleCapacity, type ScheduleRoute, type ScheduleScope, type ScheduleWeek } from "../queries";
import { rootRoute } from "./__root";
import { ScheduleTabs } from "../components/ScheduleTabs";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schedule",
  component: ScheduleScreen,
});

const scopeLabels: Record<ScheduleScope, string> = {
  tech: "Tech",
  sales: "Sales",
};

const bucketStyles = {
  RED: "bg-red-500",
  AMBER: "bg-amber-500",
  GREEN: "bg-emerald-500",
  DARK_GREEN: "bg-emerald-700",
  WARNING: "bg-fuchsia-600",
};

function normalizeName(value: string | undefined) {
  return (value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function showSupervisor(route: ScheduleRoute) {
  const routeName = normalizeName(route.route_label || route.route_name || route.route_code);
  const collaborator = normalizeName(route.collaborator_name);
  return Boolean(
    routeName
      && collaborator
      && collaborator !== routeName
      && !collaborator.startsWith(`${routeName} `),
  );
}

function ScheduleScreen() {
  const [scope, setScope] = useState<ScheduleScope>("tech");
  const query = useScheduleCapacity(scope);
  const data = query.data;
  const allWeeks = data?.routes.flatMap((route) => route.weeks) || [];

  return (
    <section className="space-y-5">
      <ScheduleTabs />
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <Badge variant="slate">Schedule</Badge>
          <h1 className="mt-3 text-2xl font-semibold text-slate-950">Schedule capacity</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Weekly fill rate by classified Vonigo route, refreshed by the schedule sync.
          </p>
        </div>
        <a
          href="https://sirgrout.vonigo.com/Schedule/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-blue-700 hover:text-blue-900"
        >
          Open Vonigo Schedule
        </a>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="inline-flex w-fit rounded-md border border-slate-200 bg-white p-1">
          {(["tech", "sales"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setScope(value)}
              className={cn(
                "h-8 rounded px-4 text-sm font-medium transition-colors",
                scope === value
                  ? "bg-slate-950 text-white"
                  : "text-slate-600 hover:bg-slate-100",
              )}
            >
              {scopeLabels[value]}
            </button>
          ))}
        </div>
        <Legend />
      </div>

      {query.isLoading ? (
        <Card><CardContent className="py-8 text-center text-sm text-slate-500">Loading schedule capacity...</CardContent></Card>
      ) : query.isError ? (
        <Card><CardContent className="py-8 text-center text-sm text-red-600">Schedule capacity could not load.</CardContent></Card>
      ) : data?.empty_reason === "no_classified_routes" ? (
        <NoClassifiedRoutes scope={scope} />
      ) : allWeeks.length === 0 ? (
        <NoScheduleData />
      ) : (
        <div className="space-y-3">
          {data?.routes.map((route) => (
            <RouteCapacityCard
              key={route.route_id}
              route={route}
              scope={scope}
              target={data.weekly_target.value}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function NoClassifiedRoutes({ scope }: { scope: ScheduleScope }) {
  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardHeader>
        <CardTitle className="text-amber-950">No {scopeLabels[scope].toLowerCase()} routes classified yet</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-amber-900">
        <p>
          Auto-discovered Vonigo routes need a Tech, Sales, or Virtual type before
          they can appear in schedule capacity.
        </p>
        <Link to="/settings/routes">
          <Button>Open Settings Routes</Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function NoScheduleData() {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-slate-600">
        No scheduled work orders are synced for the current horizon yet.
      </CardContent>
    </Card>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-xs text-slate-600">
      <Swatch className="bg-red-500" label="Under 50%" />
      <Swatch className="bg-amber-500" label="50-80%" />
      <Swatch className="bg-emerald-500" label="80-100%" />
      <Swatch className="bg-emerald-700" label="100-140%" />
      <Swatch className="bg-fuchsia-600" label="Over 140%" />
    </div>
  );
}

function RouteCapacityCard({
  route,
  scope,
  target,
}: {
  route: ScheduleRoute;
  scope: ScheduleScope;
  target: number;
}) {
  const title = route.route_label || route.route_name || route.route_code;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>
              {title}
              <span className="ml-2 text-sm font-normal text-slate-500">({route.route_code})</span>
            </CardTitle>
            {showSupervisor(route) ? (
              <p className="mt-0.5 text-xs text-slate-500">Supervised by {route.collaborator_name}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Target {target} {scope === "tech" ? "days" : "appointments"}/week</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {route.weeks.length === 0 ? (
          <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-sm text-slate-500">
            No scheduled items for this route in the current horizon.
          </div>
        ) : route.weeks.map((week) => (
          <WeekBar key={week.iso_week} week={week} scope={scope} />
        ))}
      </CardContent>
    </Card>
  );
}

function WeekBar({ week, scope }: { week: ScheduleWeek; scope: ScheduleScope }) {
  const widthPct = Math.min(Math.max(week.fill_pct, 0), 150) / 150 * 100;
  const detail = scope === "tech"
    ? `${week.days_worked || 0}d / ${week.jobs_count || 0} jobs`
    : `${week.appointments_count || 0} appointments`;

  return (
    <div className="grid gap-2 md:grid-cols-[112px_1fr_140px] md:items-center">
      <div
        className="text-xs font-medium text-slate-600"
        title={`ISO ${week.iso_week} (${week.display_label_long})`}
      >
        {week.display_label || week.iso_week.replace(/^\d{4}-/, "")}
      </div>
      <div className="relative h-7 overflow-hidden rounded bg-slate-100">
        <div
          className={cn("h-full rounded", bucketStyles[week.bucket])}
          style={{ width: `${widthPct}%` }}
        />
        <div
          className="absolute top-0 h-full border-l-2 border-dashed border-slate-400"
          style={{ left: `${100 / 150 * 100}%` }}
        />
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-slate-600 md:justify-end">
        <span className="font-semibold text-slate-950">{week.fill_pct}%</span>
        <span>{detail}</span>
      </div>
    </div>
  );
}

function Swatch({ className, label }: { className: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className={cn("h-3 w-3 rounded", className)} />
      <span>{label}</span>
    </div>
  );
}
